<template>
  <div class="c-tip-popover">
    <div ref="trigger" class="c-tip-popover__trigger" @click.stop="handleTriggerClick">
      <slot name="trigger" />
    </div>
    <div
      v-if="value"
      ref="panel"
      class="c-tip-popover__panel"
      :class="[`is-${placement}`]"
      :style="panelStyle"
      @click.stop
    >
      <div v-if="placement === 'bottom'" class="c-tip-popover__arrow c-tip-popover__arrow--top" :style="arrowStyle" />
      <div class="c-tip-popover__content">
        <slot />
      </div>
      <div v-if="placement === 'top'" class="c-tip-popover__arrow c-tip-popover__arrow--bottom" :style="arrowStyle" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CTipPopover',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'bottom',
      validator: (val) => ['top', 'bottom'].includes(val),
    },
    gap: {
      type: Number,
      default: 6,
    },
    width: {
      type: [Number, String],
      default: 295,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    manual: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      panelTop: 0,
      arrowOffset: 0,
    }
  },
  computed: {
    panelStyle() {
      const width = typeof this.width === 'number' ? `${this.width}px` : this.width
      return {
        top: `${this.panelTop}px`,
        width,
      }
    },
    arrowStyle() {
      if (!this.arrowOffset) return {}
      return {
        transform: `translateX(${this.arrowOffset}px)`,
      }
    },
  },
  watch: {
    value(val) {
      // prettier-ignore
      if (val) {
        this.$nextTick(() => {
          this.updatePosition()
          this.addPositionListeners()
        })
      }
      else {
        this.removePositionListeners()
      }
    },
  },
  mounted() {
    if (this.closeOnClickOutside) {
      document.body.addEventListener('click', this.handleBodyClick)
    }
  },
  beforeDestroy() {
    this.removePositionListeners()
    if (this.closeOnClickOutside) {
      document.body.removeEventListener('click', this.handleBodyClick)
    }
  },
  methods: {
    handleTriggerClick() {
      if (!this.manual) {
        this.$emit('input', !this.value)
      }
      this.$emit('trigger-click')
    },
    handleBodyClick() {
      if (this.value && this.closeOnClickOutside) {
        this.$emit('input', false)
      }
    },
    addPositionListeners() {
      window.addEventListener('resize', this.updatePosition)
      // capture 是为了同时捕获内层滚动容器的滚动；passive + rAF 节流则是因为
      // updatePosition 每次都要「读 rect → 写响应式数据」，直接挂在 scroll 上会让
      // 滚动被拉回主线程，并且每个事件都触发一次强制同步布局。
      window.addEventListener('scroll', this.schedulePositionUpdate, { capture: true, passive: true })
    },
    removePositionListeners() {
      window.removeEventListener('resize', this.updatePosition)
      window.removeEventListener('scroll', this.schedulePositionUpdate, true)
      if (this._positionRaf) {
        cancelAnimationFrame(this._positionRaf)
        this._positionRaf = null
      }
    },
    schedulePositionUpdate() {
      if (this._positionRaf) return
      this._positionRaf = requestAnimationFrame(() => {
        this._positionRaf = null
        this.updatePosition()
      })
    },
    updatePosition() {
      const trigger = this.$refs.trigger
      const panel = this.$refs.panel
      if (!trigger || !panel) return

      const triggerRect = trigger.getBoundingClientRect()
      const panelWidth = panel.offsetWidth
      const panelHeight = panel.offsetHeight
      const viewportCenterX = window.innerWidth / 2
      const triggerCenterX = triggerRect.left + triggerRect.width / 2
      const maxArrowOffset = Math.max(panelWidth / 2 - 16, 0)

      this.arrowOffset = Math.min(Math.max(triggerCenterX - viewportCenterX, -maxArrowOffset), maxArrowOffset)

      // prettier-ignore
      if (this.placement === 'bottom') {
        this.panelTop = triggerRect.bottom + this.gap
      }
      else {
        this.panelTop = triggerRect.top - this.gap - panelHeight
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.c-tip-popover {
  display: inline-flex;
  vertical-align: middle;

  &__trigger {
    display: inline-flex;
    line-height: 0;
  }

  &__panel {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-width: calc(100vw - 32px);
    box-sizing: border-box;
  }

  &__arrow {
    align-self: center;
    flex-shrink: 0;
    width: 0;
    height: 0;
    position: relative;
    z-index: 1;

    &--top {
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-bottom: 8px solid rgb(239, 230, 214);
      margin-bottom: -1px;
    }

    &--bottom {
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid rgb(239, 230, 214);
      margin-top: -1px;
    }
  }

  &__content {
    width: 100%;
    padding: 8px 4px;
    background: rgb(239, 230, 214);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08);
    font-family: var(--string-font);
    font-weight: var(--fw-400);
    font-size: var(--fs-12);
    line-height: var(--lh-label-16);
    color: #925e0d;
    text-align: center;
    box-sizing: border-box;

    :deep(.tip-bean) {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
